import React from "react";
import DispatchRibbonSection from "./resultsPageChrome/DispatchRibbonSection.jsx";
import {
  AlertBannerSection,
  InvalidStopsSection,
  KpiGridSection,
  RouteHeaderSection,
  RouteSequenceSection,
  SupportPanelsSection,
  SuspendedPanelSection,
} from "./resultsPageChrome/SupportSections.jsx";
import useResultsPageChromeModels from "../hooks/useResultsPageChromeModels.js";
import { buildResultsRightPanelData, publishResultsRightPanelData } from "../utils/rightPanelData.js";
import { renderPanelPortal } from "./resultsPageChrome/shared.js";

function ResultsChromeRoot({
  assignment = null,
  includeSuspendedDeliveriesInOptimization = false,
  interactions = {},
  mapRenderMeta = null,
  mountNodes = null,
  state = null,
  trip = null,
}) {
  const {
    alertBannerModel,
    dispatchRibbonModel,
    kpiGridModel,
    routeHeaderModel,
    supportPanelsModel,
    suspendedPanelModel,
  } = useResultsPageChromeModels({
    assignment,
    includeSuspendedDeliveriesInOptimization,
    mapRenderMeta,
    state,
    trip,
  });

  React.useEffect(() => {
    if (state) {
      publishResultsRightPanelData(buildResultsRightPanelData(state));
    }
  }, [state?.assignmentList, state?.planningSummary, state?.resultsData]);

  // Vider seulement au démontage : une seule publication par changement d'état.
  React.useEffect(() => () => publishResultsRightPanelData(null), []);

  if (!state || !mountNodes) {
    return null;
  }

  const chromeInteractions = interactions;

  return (
    <>
      {renderPanelPortal(
        mountNodes.alertBannerRoot,
        <AlertBannerSection interactions={chromeInteractions} model={alertBannerModel} />,
      )}
      {renderPanelPortal(
        mountNodes.dispatchRibbonRoot,
        <DispatchRibbonSection countersMountNode={mountNodes.detailViewActionsRoot} interactions={chromeInteractions} model={dispatchRibbonModel} />,
      )}
      {renderPanelPortal(
        mountNodes.routeHeaderRoot,
        <RouteHeaderSection interactions={chromeInteractions} model={routeHeaderModel} />,
      )}
      {renderPanelPortal(
        mountNodes.kpiGridRoot,
        <KpiGridSection model={kpiGridModel} inline />,
      )}
      {renderPanelPortal(
        mountNodes.supportPanelsRoot,
        <SupportPanelsSection interactions={chromeInteractions} model={supportPanelsModel} />,
      )}
      {renderPanelPortal(
        mountNodes.suspendedPanelRoot,
        <SuspendedPanelSection interactions={chromeInteractions} model={suspendedPanelModel} />,
      )}
    </>
  );
}

export default function ResultsPageChrome({
  assignment = null,
  highlightedRouteSegmentIndex = null,
  includeSuspendedDeliveriesInOptimization = false,
  interactions = {},
  mapRenderMeta = null,
  model = {},
  mountNodes = null,
  state = null,
  trip = null,
  view = "",
}) {
  if (view === "root" || (mountNodes && state)) {
    return (
      <ResultsChromeRoot
        assignment={assignment}
        includeSuspendedDeliveriesInOptimization={includeSuspendedDeliveriesInOptimization}
        interactions={{
          ...interactions,
          highlightedRouteSegmentIndex,
        }}
        mapRenderMeta={mapRenderMeta}
        mountNodes={mountNodes}
        state={state}
        trip={trip}
      />
    );
  }

  if (view === "dispatch-ribbon") {
    return <DispatchRibbonSection interactions={interactions} model={model} />;
  }

  if (view === "alert-banner") {
    return <AlertBannerSection interactions={interactions} model={model} />;
  }

  if (view === "route-header") {
    return <RouteHeaderSection interactions={interactions} model={model} />;
  }

  if (view === "kpi-grid") {
    return <KpiGridSection model={model} />;
  }

  if (view === "support-panels") {
    return <SupportPanelsSection interactions={interactions} model={model} />;
  }

  if (view === "suspended-panel") {
    return <SuspendedPanelSection interactions={interactions} model={model} />;
  }

  if (view === "route-sequence") {
    return <RouteSequenceSection interactions={interactions} model={model} />;
  }

  if (view === "invalid-stops") {
    return <InvalidStopsSection interactions={interactions} model={model} />;
  }

  return null;
}
